import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { api } from '@/services/api'

interface User {
  id: number
  email: string
  display_name?: string
  avatar_url?: string
  auth_provider: string
  created_at: string
  updated_at: string
  last_login?: string
  is_admin?: boolean
}

interface AuthContextType {
  user: User | null
  loading: boolean
  isImpersonating: boolean
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string, displayName?: string) => Promise<void>
  logout: () => Promise<void>
  updateProfile: (updates: { display_name?: string; avatar_url?: string }) => Promise<void>
  updateUser: (user: User) => void
  impersonateUser: (userId: number) => Promise<void>
  stopImpersonating: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [isImpersonating, setIsImpersonating] = useState(false)

  // Check if user is logged in on mount
  useEffect(() => {
    checkAuth()
  }, [])

  const checkAuth = async () => {
    try {
      const response = await api.get('/api/auth/me')
      setUser(response.data)
      setIsImpersonating(!!response.data.is_impersonating)
    } catch (error) {
      // User is not authenticated
      setUser(null)
      setIsImpersonating(false)
    } finally {
      setLoading(false)
    }
  }

  const login = useCallback(async (email: string, password: string) => {
    const response = await api.post('/api/auth/login', {
      email,
      password,
    })
    setUser(response.data.user)
  }, [])

  const register = useCallback(async (email: string, password: string, displayName?: string) => {
    const response = await api.post('/api/auth/register', {
      email,
      password,
      display_name: displayName,
    })
    setUser(response.data.user)
  }, [])

  const logout = useCallback(async () => {
    await api.post('/api/auth/logout')
    setUser(null)
  }, [])

  const updateProfile = useCallback(async (updates: { display_name?: string; avatar_url?: string }) => {
    const response = await api.patch('/api/auth/me', updates)
    setUser(response.data)
  }, [])

  const updateUser = useCallback((user: User) => {
    setUser(user)
  }, [])

  const impersonateUser = useCallback(async (userId: number) => {
    const response = await api.post(`/api/admin/impersonate/${userId}`)
    setIsImpersonating(true)
    // Reload user data as the impersonated user
    const meResponse = await api.get('/api/auth/me')
    setUser(meResponse.data)
  }, [])

  const stopImpersonating = useCallback(async () => {
    const response = await api.post('/api/admin/stop-impersonate')
    setIsImpersonating(false)
    // Reload user data as the admin
    const meResponse = await api.get('/api/auth/me')
    setUser(meResponse.data)
  }, [])

  const value = {
    user,
    loading,
    isImpersonating,
    login,
    register,
    logout,
    updateProfile,
    updateUser,
    impersonateUser,
    stopImpersonating,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}